// web/src/components/AvatarMenu.tsx
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import type { JSX } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { onAuthStateChanged, type User, signOut, reload } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { doc, onSnapshot, updateDoc, serverTimestamp, getDocs } from "firebase/firestore"; // add getDocs
import RankBadge from "@/components/RankBadge";
import UserChip from "@/components/UserChip";
import Icon from "@/components/Icon";
import { useTour } from "@/tour/TourProvider";
import { collection, query, where, limit } from "firebase/firestore";
import FollowRequestsModal from "@/components/follow/FollowRequestsModal";
import { getAuth, signOut as fbSignOut } from "firebase/auth";

// Toggle CTA via env (set VITE_SHOW_IGG_CTA=true to show)
const SHOW_IGG_CTA =
  (import.meta.env.VITE_SHOW_IGG_CTA ?? "false").toString().toLowerCase() === "true";

/** ----- Types ----- */
type Timestampish = { seconds?: number; nanoseconds?: number } | null;

type Preferences = {
  available_for_trading?: boolean;
  available_for_play?: boolean;
};

type Presence = {
  show_in_marketplace?: boolean;
  show_in_community_stream?: boolean;
};

type ProfileDoc = {
  // Identity
  username?: string | null;
  display_name?: string | null; // canonical
  displayName?: string | null; // legacy

  // Avatar (new primary + legacy/provider fallbacks)
  avatar_url?: string | null;
  photoURL?: string | null;

  // Cache-busting + historical
  photoUpdatedAt?: Timestampish;
  updatedAt?: Timestampish;

  // Rankings
  rank?: number | null;
  rank_title?: string | null;

  // New prefs/presence model
  preferences?: Preferences | null;
  presence?: Presence | null;

  // Legacy availability booleans (still respected if present)
  availableForTrade?: boolean;
  availableForPlay?: boolean;
};

/** ----- Small utilities ----- */
function isStr(v: unknown): v is string {
  return typeof v === "string";
}
function isNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}
function initials(text: string): string {
  return text
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
function withVersion(url: string, ver: number | null): string {
  if (!url || !ver) return url;
  const j = url.includes("?") ? "&" : "?";
  return `${url}${j}v=${ver}`;
}

// Rename or delete the local helper to avoid shadowing the imported RankBadge
function BadgeFrame({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      className={[
        "relative rounded-lg p-2",
        "bg-gradient-to-r from-[#2d1b69]/30 via-[#0b1020]/30 to-[#0a0f1f]/30",
        "border border-[#6d28d9]/40 ring-1 ring-white/5",
        "shadow-[inset_0_1px_0_rgba(255,255,255,.04)]",
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 rounded-lg ring-1 ring-[#a78bfa]/25" />
      <div className="relative">{children}</div>
    </div>
  );
}

/** ----- Component ----- */
export default function AvatarMenu(/* props */) {
  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean>(false);
  const [fbUser, setFbUser] = useState<User | null>(auth.currentUser);
  const [profile, setProfile] = useState<ProfileDoc | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [legalOpen, setLegalOpen] = useState<boolean>(false);
  const [bugOpen, setBugOpen] = useState<boolean>(false);
  // NEW: social popover state
  const [socialOpen, setSocialOpen] = useState<boolean>(false);
  const [newsOpen, setNewsOpen] = useState<boolean>(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [pendingIncoming, setPendingIncoming] = useState(0);

  // NEW: radius state and save flags
  const [tradeRadius, setTradeRadius] = useState<number>(25);
  const [playRadius, setPlayRadius] = useState<number>(25);
  const [radiusDirty, setRadiusDirty] = useState<boolean>(false);
  const radiiInitRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const bugRef = useRef<HTMLButtonElement | null>(null);
  // NEW: social button ref (anchor)
  const socialRef = useRef<HTMLButtonElement | null>(null);
  const location = useLocation();

  // Auth subscription
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFbUser(u));
    return () => unsub();
  }, []);

  // Refresh provider photoURL (non-blocking)
  useEffect(() => {
    if (!fbUser) return;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    reload(fbUser).catch(() => void 0);
  }, [fbUser]);

  // Live user doc (guard permission-denied so it doesn't loop + layout thrash)
  useEffect(() => {
    if (!fbUser) {
      setProfile(null);
      return;
    }
    let unsub: (() => void) | null = null;
    let alive = true;

    (async () => {
      try {
        // Probe read (fast, single doc). If rules block, skip listener.
        // (We read directly with onSnapshot below; probing "users" collection not needed, but this pattern allows catching denied early.)
        // If you prefer, you could use getDoc(doc(db,"users", fbUser.uid)) instead.
        await getDocs(query(collection(db, "users"), limit(1)));

        if (!alive) return;
        const dref = doc(db, "users", fbUser.uid);
        unsub = onSnapshot(
            dref,
            (snap) => {
              const data = (snap.data() ?? {}) as ProfileDoc;
              setProfile(data);
              if (!radiiInitRef.current) {
                const pr = Number((data as any)?.preferences?.radius_play_miles) || 25;
                const tr = Number((data as any)?.preferences?.radius_trading_miles) || 25;
                setPlayRadius(Math.min(100, Math.max(5, Math.round(pr / 5) * 5)));
                setTradeRadius(Math.min(100, Math.max(5, Math.round(tr / 5) * 5)));
                radiiInitRef.current = true;
              }
            },
            (err) => {
              if ((err as any)?.code === "permission-denied") {
                console.warn("[avatar] user doc permission-denied; detaching listener");
              } else {
                console.warn("[avatar] user doc snapshot error; detaching listener", err);
              }
              try { unsub?.(); } catch {}
              unsub = null;
            }
        );
      } catch (e: any) {
        if (e?.code === "permission-denied") {
          console.warn("[avatar] probe read denied; skipping user doc listener");
        } else {
          console.warn("[avatar] probe read failed; skipping user doc listener", e);
        }
      }
    })();

    return () => {
      alive = false;
      try { unsub?.(); } catch {}
    };
  }, [fbUser?.uid]);

  // Uniform row class (used below)
  const rowCls =
    "flex items-center justify-between gap-3 rounded-md border border-white/10 bg-white/5 px-2 py-2";

  // Click-away to close menu (and bug popover)
  useEffect(() => {
    function onDocPointerDown(e: MouseEvent | TouchEvent): void {
      const target = e.target as Node | null;
      if (!wrapRef.current) return;
      if (target && !wrapRef.current.contains(target)) {
        setOpen(false);
        setBugOpen(false);
        // NEW: close social popover on outside click
        setSocialOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocPointerDown);
    document.addEventListener("touchstart", onDocPointerDown);
    return () => {
      document.removeEventListener("mousedown", onDocPointerDown);
      document.removeEventListener("touchstart", onDocPointerDown);
    };
  }, []);

  // Close on route change
  useEffect(() => {
    setOpen(false);
    setBugOpen(false);
    // NEW: close social on route change
    setSocialOpen(false);
  }, [location.pathname]);

  // --- Moved hooks ABOVE any conditional render to keep hook order stable ---
  const tour = useTour();

  const isAvatarStep =
    tour.running &&
    tour.stepId != null &&
    tour.stepId.startsWith("avatar-") &&
    tour.stepId !== "avatar-menu-trigger";

  useEffect(() => {
    if (!tour.running) return;
    if (tour.stepId === "avatar-menu-trigger") {
      if (open) setOpen(false);
      return;
    }
    if (isAvatarStep && !open) setOpen(true);
    if (!isAvatarStep && tour.stepId && !tour.stepId.startsWith("avatar-") && open) {
      setOpen(false);
    }
  }, [tour.running, tour.stepId, isAvatarStep, open]);
  // -------------------------------------------------------------------------

  // Identity label
  const usernameText: string =
    (isStr(profile?.username) && profile!.username!) ||
    (isStr(profile?.display_name) && profile!.display_name!) ||
    (isStr(profile?.displayName) && profile!.displayName!) ||
    fbUser?.displayName ||
    "Player";

  // Rank label
  const rankLabel: string =
    (isStr(profile?.rank_title) && profile!.rank_title!) ||
    (isNum(profile?.rank) ? `Rank ${profile!.rank}` : "Unranked");
  // NEW: points from non-resetting system (fallback to legacy total_score)
  const rankPoints: number | null =
    (isNum(profile?.rank_points_total) && Number(profile!.rank_points_total)) ??
    (isNum(profile?.total_score) && Number(profile!.total_score)) ??
    null;

  const version: number | null =
    profile?.photoUpdatedAt?.seconds ?? profile?.updatedAt?.seconds ?? null;

  const avatarSrc: string | null = useMemo(() => {
    const pick = (): string | null => {
      if (isStr(profile?.avatar_url) && profile!.avatar_url!.trim() !== "")
        return profile!.avatar_url!;
      if (isStr(profile?.photoURL) && profile!.photoURL!.trim() !== "")
        return profile!.photoURL!;
      const prov = fbUser?.providerData?.find(
        (p) => isStr(p.photoURL) && p.photoURL.trim() !== "",
      )?.photoURL;
      if (prov) return prov;
      if (isStr(fbUser?.photoURL) && fbUser!.photoURL!.trim() !== "")
        return fbUser!.photoURL!;
      return null;
    };
    const raw = pick();
    return raw ? withVersion(raw, version) : null;
  }, [profile?.avatar_url, profile?.photoURL, version, fbUser]);

  const availableForTrading: boolean = Boolean(
    profile?.preferences?.available_for_trading ??
      profile?.availableForTrade ??
      false,
  );
  const availableForPlay: boolean = Boolean(
    profile?.preferences?.available_for_play ??
      profile?.availableForPlay ??
      false,
  );
  // LGS flag for menu visibility (defined once; used below)
  const isLGSUser: boolean = Boolean((profile as any)?.isLGS);

  async function toggleTrading(): Promise<void> {
    if (!fbUser) return;
    setSaving(true);
    setError(null);
    try {
      await updateDoc(doc(db, "users", fbUser.uid), {
        "preferences.available_for_trading": !availableForTrading,
        "presence.show_in_marketplace": !availableForTrading,
        updatedAt: serverTimestamp(),
      });
      setOpen(false); // keep existing behavior
      navigate("/dashboard");
    } catch {
      setError("Could not update trading status.");
    } finally {
      setSaving(false);
    }
  }

  async function togglePlay(): Promise<void> {
    if (!fbUser) return;
    setSaving(true);
    setError(null);
    try {
      await updateDoc(doc(db, "users", fbUser.uid), {
        "preferences.available_for_play": !availableForPlay,
        "presence.show_in_community_stream": !availableForPlay,
        updatedAt: serverTimestamp(),
      });
      setOpen(false);
      navigate("/");
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong");
    } finally {
      setSaving(false);
    }
  }

  // NEW: save radii (debounced and on-close)
  const saveRadii = useCallback(async () => {
    if (!fbUser || !radiiInitRef.current || !radiusDirty) return;
    try {
      setSaving(true);
      await updateDoc(doc(db, "users", fbUser.uid), {
        "preferences.radius_trading_miles": tradeRadius,
        "preferences.radius_play_miles": playRadius,
        updatedAt: serverTimestamp(),
      });
      setRadiusDirty(false);
    } catch {
      setError("Could not save radius preferences.");
    } finally {
      setSaving(false);
    }
  }, [fbUser, tradeRadius, playRadius, radiusDirty]);

  // Debounce: 5s after last change
  useEffect(() => {
    if (!radiusDirty) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      saveRadii();
    }, 5000) as unknown as number;
    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [radiusDirty, tradeRadius, playRadius, saveRadii]);

  // Save on menu close
  useEffect(() => {
    if (!open && radiusDirty) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      saveRadii();
    }
  }, [open, radiusDirty, saveRadii]);

  // Small helpers
  const prettyMi = (n: number) => `${n} mi`;
  const onTradeRadius = (n: number) => {
    radiiInitRef.current = true;
    setTradeRadius(n);
    setRadiusDirty(true);
  };
  const onPlayRadius = (n: number) => {
    radiiInitRef.current = true;
    setPlayRadius(n);
    setRadiusDirty(true);
  };

  // Build bug-report mailto (pre-fill helpful context)
  const bugMailtoHref: string = useMemo(() => {
    const email = "support@spellgather.com";
    const subj = encodeURIComponent("Spellgather bug report");
    const url =
      typeof window !== "undefined" && window.location
        ? window.location.href
        : "(n/a)";
    const agent =
      typeof navigator !== "undefined" ? navigator.userAgent : "(n/a)";
    const user = profile?.username || fbUser?.uid || "anonymous";
    const body = encodeURIComponent(
      [
        "Describe the issue:",
        "",
        "Steps to reproduce:",
        "1.",
        "2.",
        "3.",
        "",
        "Expected result:",
        "",
        "Actual result:",
        "",
        `URL: ${url}`,
        `User: ${user}`,
        `Browser: ${agent}`,
      ].join("\n"),
    );
    return `mailto:${email}?subject=${subj}&body=${body}`;
  }, [profile?.username, fbUser?.uid, location.pathname]);

  // Treat Abort/Cancel/Dismiss errors from native share as user-cancel (no toast)
  function isShareCancel(err: unknown): boolean {
    const anyErr = err as any;
    const name = String(anyErr?.name || "");
    const msg = String(anyErr?.message || anyErr || "").toLowerCase();
    if (["AbortError", "NotAllowedError", "OperationError", "CanceledError", "CancelError"].includes(name)) return true;
    return msg.includes("abort") || msg.includes("canceled") || msg.includes("cancelled") || msg.includes("dismiss");
  }

  // Minimal username hook (kept; still runs every render before JSX)
  function useUserUsername(): string | null {
    const [username, setUsername] = useState<string | null>(null);
    useEffect(() => {
      const uid = auth.currentUser?.uid;
      if (!uid) { setUsername(null); return; }

      let unsub: (() => void) | null = null;
      let alive = true;

      (async () => {
        try {
          // Probe (optional)
            await getDocs(query(collection(db, "users"), limit(1)));
          if (!alive) return;
          unsub = onSnapshot(
            doc(db, "users", uid),
            (snap) => {
              const u = snap.data() as { username?: string | null } | undefined;
              setUsername((u?.username ?? null) ? String(u!.username) : null);
            },
            (err) => {
              if ((err as any)?.code === "permission-denied") {
                console.warn("[avatar] username listener permission-denied; skipping");
              } else {
                console.warn("[avatar] username listener error; skipping", err);
              }
              try { unsub?.(); } catch {}
              unsub = null;
            }
          );
        } catch (e: any) {
          if (e?.code === "permission-denied") {
            console.warn("[avatar] username probe denied; no listener");
          } else {
            console.warn("[avatar] username probe failed", e);
          }
        }
      })();

      return () => {
        alive = false;
        try { unsub?.(); } catch {}
      };
    }, []);
    return username;
  }
  const username = useUserUsername();

  const effectiveUsername = useMemo(() => {
    const uname =
      (typeof profile?.username === "string" && profile.username) ||
      (typeof username === "string" && username) ||
      null;
    return uname ? uname.trim() : null;
  }, [profile?.username, username]);

  const PUBLIC_BASE_URL = "https://www.spellgather.com";
  const shareUrl = effectiveUsername
    ? `${PUBLIC_BASE_URL}/${encodeURIComponent(effectiveUsername)}`
    : null;

  const onShareProfile = useCallback(async () => {
    if (!shareUrl) {
      setNotice("Set a username in Settings to share your profile.");
      navigate("/settings");
      return;
    }
    const url = shareUrl;
    const show = (msg: string) => {
      setNotice(msg);
      window.setTimeout(() => setNotice(null), 1800);
    };
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Spellgather profile", url });
        show("Share dialog opened");
        return;
      }
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(url);
        show("Profile link copied");
        return;
      }
      window.prompt("Copy your profile URL:", url);
    } catch (e) {
      if (isShareCancel(e)) return; // user canceled share
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          await navigator.clipboard.writeText(url);
          show("Profile link copied");
          return;
        }
      } catch {
        // ignore
      }
      // Final fallback: show prompt
      window.prompt("Copy your profile URL:", url);
    }
  }, [shareUrl, navigate]);

  const profileUrl =
    isStr(profile?.username) && profile!.username
      ? `/${encodeURIComponent(profile!.username!)}`
      : null;

  const panelId = "avatar-popover";

  function startProductTour() {
    setOpen(false);
    (tour as any)?.start?.();
    (tour as any)?.startTour?.();
    (tour as any)?.begin?.();
  }

  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      // save pending radius changes before sign out
      if (radiusDirty) {
        await saveRadii();
      }
      // Use the old flow (signOut(auth)) for parity, keep navigate + hard redirect fallback
      await signOut(auth);
      setOpen(false);
      navigate("/", { replace: true });
      // Hard fallback to ensure we land on home like the old menu
      setTimeout(() => {
        if (typeof window !== "undefined" && window.location.pathname !== "/") {
          window.location.replace("/");
        }
      }, 0);
    } catch (e: any) {
      setError(e?.message || "Sign out failed");
    } finally {
      setSigningOut(false);
    }
  }, [signingOut, radiusDirty, saveRadii, navigate]);

  // NEW: Upgrade LGS modal state
  const openUpgrade = useCallback(() => {
    // Close the menu first
    setOpen(false);
    // Navigate to Settings and request the LGS modal open
    navigate("/settings?modal=lgsUpgrade", { state: { lgsUpgrade: true } });
  }, [navigate, setOpen]);

  // NEW: centralized social links (moved from footer)
  const SOCIAL_LINKS = [
    { alt: "X",        href: "https://x.com/spellgather",               src: "/images/icons/twitter.svg" },
    { alt: "Discord",  href: "https://discord.gg/spellgather",          src: "/images/icons/discord.svg" },
    { alt: "Twitch",   href: "https://twitch.tv/spellgather",           src: "/images/icons/twitch.svg" },
    { alt: "Facebook", href: "https://www.facebook.com/profile.php?id=61579819631986", src: "/images/icons/fb.svg" },
    { alt: "Instagram",href: "https://instagram.com/spellgather",       src: "/images/icons/instagram.svg" },
    { alt: "Reddit",   href: "https://reddit.com/r/spellgather",        src: "/images/icons/reddit.svg" },
    { alt: "TikTok",   href: "https://www.tiktok.com/@spellgather",     src: "/images/icons/tiktok.svg" },
    { alt: "YouTube",  href: "https://www.youtube.com/@spellgather",    src: "/images/icons/youtube.svg" },
    { alt: "Patreon",  href: "https://patreon.com/Spellgather",         src: "/images/bob_money.png" },
    { alt: "Support",  href: "mailto:support@spellgather.com",          src: "/images/icons/email.svg" },
  ];

  return (
    <>
      <div className="relative avatar-menu text-white" ref={wrapRef}>
        {!fbUser && (
          <Link
            to="/"
            className="rounded-full border border-gray-700 px-3 py-1 text-sm text-white/90 hover:bg-white/5"
          >
            Sign in
          </Link>
        )}

        {fbUser && (
          <>
            {/* Trigger button */}
            <button
              data-tour="avatar-menu-trigger"
              className={[
                "relative grid place-items-center overflow-hidden rounded-full border border-gray-700 transition-transform",
                open ? "scale-110 ring-2 ring-white/20" : "hover:bg-white/5",
                "w-16 h-16 sm:w-12 sm:h-12",
              ].join(" ")}
              onClick={() => {
                // Allow manual toggle only if not locked by tour
                if (isAvatarStep) return;
                setOpen(o => !o);
              }}
              aria-haspopup="menu"
              aria-expanded={open ? "true" : "false"}
              aria-controls={panelId}
              aria-label="Open profile menu"
              type="button"
              title="Open profile menu"
            >
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt="Profile image"
                  className="h-full w-full object-cover"
                  referrerPolicy="no-referrer"
                  decoding="async"
                />
              ) : (
                <span className="grid h-full w-full place-items-center bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white text-xs font-bold select-none">
                  {initials(usernameText)}
                </span>
              )}
            </button>

            {open && (
              <div
                id={panelId}
                className="absolute right-0 mt-2 w-[min(92vw,380px)] rounded-lg border border-gray-700 bg-gray-900/95 backdrop-blur-sm shadow-xl overflow-hidden z-50"
                data-tour="avatar-menu-surface"
              >
                {/* Header */}
                <div
                  className="flex items-center justify-between px-3 py-2 border-b border-gray-800/80 bg-gray-900/60"
                  data-tour="avatar-header"
                >
                  {/* Left: avatar + username */}
                  <div className="flex-1 min-w-0">
                    <RankBadge
                      title={profile?.rank_title || "Initiate"}
                      points={
                        typeof profile?.rank_points_total === "number"
                          ? profile.rank_points_total
                          : typeof profile?.total_score === "number"
                          ? profile.total_score
                          : 0
                      }
                      uid={fbUser?.uid || null}
                      username={(typeof profile?.username === "string" && profile.username) || null}
                      size="small"
                      className="w-full"
                      showPoints
                    />
                  </div>
                  {/* Right: Social + Share + Bug + Settings */}
                  <div className="flex items-center gap-2 ml-2">
                    {/* NEW: Social popover trigger */}
                    <div className="relative">
                      <button
                        ref={socialRef}
                        type="button"
                        onClick={() => setSocialOpen((v) => !v)}
                        className="p-1.5 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-600"
                        aria-haspopup="menu"
                        aria-expanded={socialOpen ? "true" : "false"}
                        title="Spellgather on social"
                      >
                        <Icon name="social" size={20} className="text-white/90" />
                      </button>
                      {socialOpen && (
                        <div
                          className="absolute right-0 mt-2 w-[220px] rounded-md border border-gray-700 bg-gray-900 shadow-xl overflow-hidden z-50"
                          role="menu"
                        >
                          <div className="grid grid-cols-4 gap-3 px-3 py-3">
                            {SOCIAL_LINKS.map((i) => (
                              <a
                                key={i.alt}
                                href={i.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center rounded hover:bg-white/5 p-1.5"
                                title={i.alt}
                                onClick={() => setSocialOpen(false)}
                              >
                                <img
                                  src={i.src}
                                  alt={i.alt}
                                  className="block w-5 h-5"
                                  loading="lazy"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Share profile (kept; moved right of Social) */}
                    <button
                      type="button"
                      onClick={onShareProfile}
                      data-tour="avatar-share-profile"
                      className="p-1.5 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-600"
                      aria-label="Share public profile link"
                      title="Share profile"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white/90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l5-5m0 0l5 5m-5-5v12" />
                      </svg>
                    </button>

                    {/* Bug icon (kept) */}
                    <div className="relative">
                      <button
                        ref={bugRef}
                        type="button"
                        onClick={() => setBugOpen((v) => !v)}
                        data-tour="avatar-bug-report"
                        className="p-2 rounded-md border border-white/10 hover:bg-white/5 text-white"
                        aria-haspopup="menu"
                        aria-expanded={bugOpen ? "true" : "false"}
                        title="Report a bug"
                      >
                        <Icon name="bug" size={22} style={{ color: "#ffffffff" }} className="text-white" title="bug" />
                      </button>
                      {bugOpen && (
                        <div
                          className="absolute right-0 mt-2 w-52 rounded-md border border-gray-700 bg-gray-900 shadow-xl overflow-hidden z-50"
                          role="menu"
                        >
                          <a href={bugMailtoHref} className="block px-3 py-2 text-sm text-white/90 hover:bg-white/5">
                            Email support
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              const ctx = {
                                url: typeof window !== "undefined" ? window.location.href : "(n/a)",
                                path: location.pathname,
                                user: profile?.username || fbUser?.uid || "anonymous",
                                userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "(n/a)",
                                source: "avatar-menu",
                              };
                              setBugOpen(false);
                              setOpen(false);
                              navigate("/support", { state: ctx });
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-white/90 hover:bg-white/5"
                          >
                            Guided bug report
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Settings gear (kept) */}
                    <Link
                      to="/settings"
                      className="p-1.5 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-600"
                      aria-label="Settings"
                      title="Settings"
                    >
                      <Icon name="settings" size={20} className="text-white/90" />
                    </Link>
                  </div>

                  {error && <div className="text-xs text-amber-300 pt-1">{error}</div>}
                  {notice && <div className="text-xs text-emerald-300 pt-1">{notice}</div>}
                </div>

                {/* Availability & Radius (kept) */}
                <div className="px-3 py-3 space-y-3" data-tour="avatar-availability-row">
                  {/* Indiegogo CTA (toggleable, above Trade row) */}
                  {SHOW_IGG_CTA && (
                    <div className={rowCls}>
                      <a
                        href="https://igg.me/at/spellgather"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full"
                        title="Support us via our Indiegogo Campaign"
                      >
                        <span
                          className={[
                            "block w-full text-center px-3 py-2 text-sm font-semibold",
                            "rounded-md border border-rose-400/40 bg-rose-500/10 hover:bg-rose-500/15",
                            "text-rose-200"
                          ].join(" ")}
                        >
                          Support us via our Indiegogo Campaign
                        </span>
                      </a>
                    </div>
                  )}

                  {/* Trade row */}
                  <div className={rowCls}>
                    <button
                      type="button"
                      onClick={() => void toggleTrading()}
                      disabled={saving}
                      data-tour="avatar-trade-toggle"
                      className={[
                        "px-3 py-1 text-xs rounded-md border transition",
                        availableForTrading
                          ? "bg-[#3b82f6]/80 border-[#60a5fa] text-white"
                          : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                      ].join(" ")}
                      title="Available for trade"
                    >
                      Trade
                    </button>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="range"
                        min={5}
                        max={100}
                        step={5}
                        value={tradeRadius}
                        onChange={(e) => onTradeRadius(Number(e.currentTarget.value))}
                        className="flex-1 accent-[#60a5fa] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full"
                        aria-label="Trade radius"
                      />
                      <span className="w-12 text-right text-xs text-white/80">{prettyMi(tradeRadius)}</span>
                    </div>
                  </div>

                  {/* Play row */}
                  <div className={rowCls}>
                    <button
                      type="button"
                      onClick={() => void togglePlay()}
                      disabled={saving}
                      data-tour="avatar-play-toggle"
                      className={[
                        "px-3 py-1 text-xs rounded-md border transition",
                        availableForPlay
                          ? "bg-[#6d28d9]/80 border-[#a78bfa] text-white"
                          : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                      ].join(" ")}
                      title="Available for play"
                    >
                      Play
                    </button>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="range"
                        min={5}
                        max={100}
                        step={5}
                        value={playRadius}
                        onChange={(e) => onPlayRadius(Number(e.currentTarget.value))}
                        className="flex-1 accent-[#a78bfa] [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full"
                        aria-label="Play radius"
                      />
                      <span className="w-12 text-right text-xs text-white/80">{prettyMi(playRadius)}</span>
                    </div>
                  </div>
                </div>

                {/* Primary navigation */}
                <div className="px-3 py-3 border-t border-gray-800/80">
                  {/* Product Tour */}
                  <div className="px-3 mt-3">
                    <div className={rowCls}>
                      <button
                        type="button"
                        data-tour="avatar-tour-button"
                        onClick={() => {
                          setOpen(false);
                          tour.startTour("welcome");
                        }}
                        className="w-full flex items-center justify-between text-sm text-white/90"
                        title="Start the product walkthrough"
                      >
                        <span className="font-semibold text-emerald-300">Product Tour</span>
                        <span className="text-[10px] uppercase tracking-wide text-emerald-400/70">Start</span>
                      </button>
                    </div>
                  </div>

                  {/* Upgrade to LGS (divider before) */}
                  {!isLGSUser && (
                    <div className="px-3 mt-3 border-t border-gray-800/80">
                      <div className={rowCls}>
                        <button
                          type="button"
                          data-tour="avatar-upgrade-lgs"
                          onClick={openUpgrade}
                          className="w-full flex items-center justify-between text-sm text-white/90"
                          title="Upgrade your account to Local Game Store"
                        >
                          <span className="font-semibold">Upgrade to LGS</span>
                          <span className="text-[10px] uppercase tracking-wide">Open</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Legal Stuff */}
                <div className="border-t border-gray-800" data-tour="avatar-legal-toggle">
                  <div className="px-3 mt-3">
                    <div className={rowCls}>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between text-sm text-white/90"
                        aria-expanded={legalOpen ? "true" : "false"}
                        aria-controls="legal-links"
                        onClick={() => setLegalOpen((v) => !v)}
                        title="Toggle legal links"
                      >
                        <span className="text-[#a78bfa] font-semibold">Legal Stuff</span>
                        <svg
                          className={["h-4 w-4 transition-transform", legalOpen ? "rotate-180" : "rotate-0"].join(" ")}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.133l3.71-2.904a.75.75 0 0 1 .94 1.17l-4.2 3.29a.75.75 0 0 1-.02-1.07z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <ul
                    id="legal-links"
                    className={[
                      "overflow-hidden transition-[max-height] duration-200 ease-in-out text-sm text-white/90",
                      legalOpen ? "max-h-80 mt-2" : "max-h-0",
                    ].join(" ")}
                  >
                    <MenuLink to="/termsack" label="Terms & Acknowledgment" />
                    <MenuLink to="/legal/terms" label="Terms of Service" />
                    <MenuLink to="/legal/privacy" label="Privacy Policy" />
                    <MenuLink to="/legal/community" label="Community Guidelines" />
                    <MenuLink to="/legal/data" label="Data Sharing & Protection" />
                    <MenuLink to="/legal/copyright" label="Copyright & Fan Content" />
                    <MenuLink to="/legal/dmca" label="DMCA Policy" />
                  </ul>
                </div>

                {/* Sign out (divider before) */}
                <div className="px-3 mt-3 border-t border-gray-800/80">
                  <div className={rowCls}>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      disabled={signingOut}
                      data-tour="avatar-signout-button"
                      className="w-full flex items-center justify-between text-sm text-white/90 disabled:opacity-60"
                      title="Sign out"
                    >
                      <span className="font-semibold text-red-300">Sign out</span>
                      <span className="text-[10px] uppercase tracking-wide text-red-400/70">
                        {signingOut ? "Signing out…" : "Logout"}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Footer: provenance line (kept) */}
                <div className="px-4 py-2 border-t border-gray-800 text-xs flex flex-col gap-2">
                  {/* Majestic purple provenance row (kept) */}
                  <div
                    className={[
                      "flex items-center gap-2 rounded-md px-2 py-2",
                      "bg-gradient-to-r from-[#6d28d9]/20 via-[#7c3aed]/15 to-[#a78bfa]/10",
                      "border border-[#7c3aed]/30 text-[#c4b5fd]",
                    ].join(" ")}
                  >
                    <span className="font-medium">Proudly made in</span>
                    <img
                      src="/images/chicago-icon.png"
                      alt="Chicago"
                      className="h-8 w-auto inline-block"
                      loading="lazy"
                    />
                    <span>&</span>
                    <img
                      src="/images/pilsen.png"
                      alt="Pilsen"
                      className="h-6 w-auto inline-block transition-transform duration-100 ease-out hover:scale-150"
                      loading="lazy"
                    />
                    <span>with</span>
                    <span aria-hidden="true" className="inline-flex">
                      <svg
                        viewBox="0 0 16 16"
                        className="h-5 w-5"
                        shapeRendering="crispEdges"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 14s-5.5-3.3-7-6.5C.1 5.2 1.2 3 3.4 3 4.7 3 6 3.9 6.6 5c.6-1.1 1.9-2 3.2-2 2.2 0 3.3 2.2 2.4 4.5C13.5 10.7 8 14 8 14z"
                          fill="#ef4444"
                        />
                      </svg>
                    </span>
                  </div>

                  {/* REMOVED: Social icons row (moved to Social popover in header) */}
                  {/* Copyright row (kept) */}
                  <div className="flex items-center justify-between text-[11px] text-white/80 px-1">
                    <a
                      href="https://www.losfrancosinc.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#c4b5fd] hover:underline font-medium"
                      title="Spellgather, LLC"
                    >
                      Spellgather, LLC
                    </a>
                    <span>© 2025</span>
                  </div>
                </div>
              </div>
            )}

            {/* Follow Requests Modal (kept) */}
            <FollowRequestsModal open={requestsOpen} onOpenChange={setRequestsOpen} />

            {/* NEW: Upgrade to LGS Modal mount (uncomment import above if available) */}
            {/* <UpgradeLGSModal open={upgradeOpen} onOpenChange={setUpgradeOpen} /> */}
          </>
        )}
      </div>
    </>
  );
}

/** ----- Small UI pieces ----- */
function MenuLink({ to, label }: { to: string; label: string }): JSX.Element {
  return (
    <li>
      <Link
        className="block px-3 py-2 hover:bg-white/5 hover:text-[#c4b5fd]"
        to={to}
      >
        {label}
      </Link>
    </li>
  );
}
